import { mdToHtml } from '@buildwise/libs/helpers';
import {
  GEN_AI_SERVICE_URL,
  GEN_AI_SERVICE_ENDPOINTS,
  PythonFilesAPI,
} from '@buildwise/libs/services';
import useFetchEventSource from '@buildwise/pages/search/hooks/useFetchEventSource';
import useUserData from 'hooks/useUserData';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  checkURLAccessibility,
  extractPlainText,
  extractImagesFromHtml,
  catchErrorMessage,
  convertWebpToJpegOrPng,
} from 'utility/helpers';
import useWriteWithAIForm, {
  DEFAULT_TEXT_FIELD,
  WriteWithAIFormValues,
} from './useWriteWithAIForm';
import { AttachmentType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { IMAGE_TYPES } from 'components/utils/constants';
import axios from 'axios';

export enum WriteWithAITabName {
  FILE = 'FILE',
  TEXT = 'TEXT',
  URL = 'URL',
}

export type WriteWithAIOnSelectOptions = {
  title?: string;
  description?: string;
  embeddedAttachments?: AttachmentType[];
};

function useWriteWithAI({
  defaultPrompt = '',
  onGeneratePost,
}: {
  defaultPrompt?: string;
  onGeneratePost: (options: {
    title: string;
    description: string;
    attachments: AttachmentType[];
  }) => void;
}) {
  const { companyId } = useUserData();
  const [openWriteWithAIModal, setOpenWriteWithAIModal] = useState(false);
  const [tab] = useState<WriteWithAITabName>(WriteWithAITabName.TEXT);
  const { formState } = useWriteWithAIForm({ tab, defaultPrompt });
  const { getValues, handleSubmit, reset, setValue } = formState;
  const { fetchSSE, response, setResponse } = useFetchEventSource();

  useEffect(() => {
    const { loading, genAIResponse, metadata, isStreaming } = response;
    const { text } = getValues();
    setValue(`text.${text.length - 1}.loading`, loading);
    setValue(`text.${text.length - 1}.genAIResponse`, genAIResponse);
    setValue(`text.${text.length - 1}.isStreaming`, isStreaming);
    setValue(`text.${text.length - 1}.articleMetaData.title`, metadata.title);
    setValue(`text.${text.length - 1}.articleMetaData.description`, metadata.description);
    setValue(`text.${text.length - 1}.articleMetaData.attachments`, metadata.attachments);
  }, [response]);

  const closeModal = useCallback(() => {
    setOpenWriteWithAIModal(false);
    reset();
  }, []);

  const handleGenerate = useCallback(
    async (values: WriteWithAIFormValues) => {
      try {
        // Reset previous response
        const { text, url, files } = values;
        setResponse((prevState) => ({
          ...prevState,
          genAIResponse: '',
          metadata: { ...prevState.metadata, title: '', description: '' },
        }));
        setValue(`text.${text.length - 1}.genAIResponse`, '', {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(`text.${text.length - 1}.articleMetaData.title`, '', {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(`text.${text.length - 1}.articleMetaData.description`, '', {
          shouldDirty: true,
          shouldValidate: true,
        });

        const lastPrompt = text[text.length - 1];

        // validating the url
        if (tab === WriteWithAITabName.URL) {
          const isAccessible = await checkURLAccessibility(url);
          if (!isAccessible) {
            throw new Error('Please enter a valid URL');
          }
        }

        if ([WriteWithAITabName.TEXT, WriteWithAITabName.URL].includes(tab)) {
          const payload = {
            companyId,
            ...(tab === WriteWithAITabName.TEXT && {
              summary_text: extractPlainText(lastPrompt.text),
            }),
            ...(tab === WriteWithAITabName.URL && { web_url: url }),
            stream_response: true,
          };
          const _url = `${GEN_AI_SERVICE_URL}${GEN_AI_SERVICE_ENDPOINTS.GENERATE_ARTICLE_SUMMARY}`;

          fetchSSE(_url, payload);

          if (tab === WriteWithAITabName.TEXT) {
            setValue(`text.${text.length - 1}.isLoadingFiles`, true);
            let attachments = await extractImagesFromHtml(
              lastPrompt.text,
              async (url, fileNameWithoutExtension) => {
                let blobContent = new Blob();
                try {
                  blobContent = (await axios.get(url, { responseType: 'blob' })).data;
                } catch (error) {
                  const { data: s3Url } = await PythonFilesAPI.getExternalFileUrlWithProxyAPI(url);
                  blobContent = (await axios.get(s3Url, { responseType: 'blob' })).data;
                }
                let blob = new Blob([blobContent], {
                  type: IMAGE_TYPES.includes(blobContent.type) ? blobContent.type : 'image/jpeg',
                });

                // If the image is .webp and needs conversion
                if (blob.type === 'image/webp') {
                  blob = await convertWebpToJpegOrPng(blob, 'image/jpeg');
                }

                return new File(
                  [blob],
                  `${fileNameWithoutExtension}.${blob.type.includes('/') ? blob.type?.split('/')?.[1] : 'jpeg'}`,
                  {
                    type: blob.type,
                  },
                );
              },
            );

            setValue(`text.${text.length - 1}.isLoadingFiles`, false);
            setValue(`text.${text.length - 1}.embeddedAttachments`, attachments, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        } else {
          // Reset previous response
          setResponse((prevState) => ({
            ...prevState,
            genAIResponse: '',
            metadata: { ...prevState.metadata, title: '', description: '', attachments: [] },
          }));
          setValue(`text.${text.length - 1}.articleMetaData.attachments`, [], {
            shouldDirty: true,
            shouldValidate: true,
          });
          const formBody = new FormData();
          formBody.append('file', files[0].file);
          const _url = `${GEN_AI_SERVICE_URL}${GEN_AI_SERVICE_ENDPOINTS.GENERATE_ARTICLE_SUMMARY_FROM_FILE.replace(':company_id', companyId)}`;

          fetchSSE(_url, formBody, true);
        }
      } catch (error) {
        console.warn('WRITE_WITH_AI_ERROR', error);
        toast.error(catchErrorMessage(error));
      }
    },
    [tab],
  );

  const onGenerate = useCallback(handleSubmit(handleGenerate), [handleGenerate]);

  useEffect(() => {
    if (defaultPrompt.trim()) {
      onGenerate();
    }
  }, [defaultPrompt, onGenerate]);

  const onSelect = useCallback(
    async ({
      title = '',
      description = '',
      embeddedAttachments = [],
    }: WriteWithAIOnSelectOptions) => {
      const { files = [] } = getValues();
      onGeneratePost({
        title: title,
        description: await mdToHtml(description),
        attachments:
          tab === WriteWithAITabName.TEXT
            ? embeddedAttachments
            : tab === WriteWithAITabName.FILE
              ? files
              : [],
      });
      // closeModal();
    },
    [],
  );

  const onRetry = useCallback(() => {
    const { text } = getValues();
    setValue('text', [...text, { ...DEFAULT_TEXT_FIELD, text: text[text.length - 1].text }]);
  }, []);

  return {
    formState,
    openWriteWithAIModal,
    tab,
    closeModal,
    onGenerate,
    onSelect,
    onRetry,
    setOpenWriteWithAIModal,
  };
}

export default useWriteWithAI;
